import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { ProjectAPI } from 'api/project';
import {
  ProjectBaseline,
  ProjectBaselineCreateRequest,
  ProjectBaselineUpdateRequest,
  ProjectBaselineWriteResponse,
} from 'api/project/types';
import { GENERAL_API_STALE } from 'config/query-config';

export const getProjectBaselineQueryKey = (projectId: number) => [
  'project-baselines',
  projectId,
];

export const useCreateProjectBaselineMutation = (
  projectId: number,
): UseMutationResult<
  ProjectBaselineWriteResponse,
  unknown,
  ProjectBaselineCreateRequest,
  { previous: ProjectBaseline[] }
> => {
  const queryClient = useQueryClient();
  const queryKey = getProjectBaselineQueryKey(projectId);

  return useMutation<
    ProjectBaselineWriteResponse,
    unknown,
    ProjectBaselineCreateRequest,
    { previous: ProjectBaseline[] }
  >((payload) => ProjectAPI.createProjectBaseline(projectId, payload), {
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);

      const previous =
        queryClient.getQueryData<ProjectBaseline[]>(queryKey) || [];

      return { previous };
    },

    onSuccess: async (data, _, context) => {
      const prev = context?.previous ?? [];

      queryClient.setQueryData<ProjectBaseline[]>(queryKey, [...prev, data]);
    },

    // onSettled: async (data, _, req, context) => {
    //   const prev = context?.previous ?? [];

    //   queryClient.setQueryData<ProjectBaseline[]>(queryKey, [...prev, req]);
    // },
  });
};

export const useUpdateProjectBaselineMutation = (
  projectId: number,
): UseMutationResult<
  ProjectBaselineWriteResponse,
  unknown,
  ProjectBaselineUpdateRequest,
  { previous: ProjectBaseline[] }
> => {
  const queryClient = useQueryClient();
  const queryKey = getProjectBaselineQueryKey(projectId);

  return useMutation<
    ProjectBaselineWriteResponse,
    unknown,
    ProjectBaselineUpdateRequest,
    { previous: ProjectBaseline[] }
  >(
    ({ baselineId, ...payload }) =>
      ProjectAPI.updateProjectBaseline(projectId, Number(baselineId), payload),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(queryKey);

        const previous =
          queryClient.getQueryData<ProjectBaseline[]>(queryKey) || [];

        return { previous };
      },

      onSuccess: async (data, request, context) => {
        const prev = context?.previous ?? [];
        const result = prev.map((_baseline) =>
          _baseline.baselineId !== request.baselineId ? _baseline : data,
        );

        queryClient.setQueryData<ProjectBaseline[]>(queryKey, [...result]);
      },
    },
  );
};

export const useDeleteProjectBaselineMutation = (
  projectId: number,
): UseMutationResult<
  ProjectBaselineWriteResponse,
  unknown,
  number,
  { previous: ProjectBaseline[] }
> => {
  const queryClient = useQueryClient();
  const queryKey = getProjectBaselineQueryKey(projectId);

  return useMutation<
    ProjectBaselineWriteResponse,
    unknown,
    number,
    { previous: ProjectBaseline[] }
  >((baselineId) => ProjectAPI.deleteProjectBaseline(projectId, baselineId), {
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);

      const previous =
        queryClient.getQueryData<ProjectBaseline[]>(queryKey) || [];

      return { previous };
    },

    onSuccess: async (data, baselineId, context) => {
      const prev = context?.previous ?? [];
      const result = prev.map((_baseline) =>
        _baseline.baselineId !== baselineId
          ? _baseline
          : {
              ..._baseline,
              deleted: true,
            },
      );

      queryClient.setQueryData<ProjectBaseline[]>(queryKey, [...result]);
    },
  });
};

export const useProjectBaselineQuery = (
  projectId: number,
): UseQueryResult<ProjectBaseline[]> => {
  return useQuery<ProjectBaseline[]>(
    getProjectBaselineQueryKey(projectId),
    async () => {
      const baselines = await ProjectAPI.getProjectBaselines(projectId);

      return baselines.sort((a, b) => a.createdAt - b.createdAt);
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      staleTime: GENERAL_API_STALE,
    },
  );
};
